<template>
  <div>
    <vx-card>
      <div class="vx-row mb-4">
        <div class="vx-col w-full flex justify-between items-center">
          <h4>Configurações I4Pro</h4>
          <div class="flex">
            <vs-button
              color="success"
              class="mr-2"
              :disabled="!hasPendingUpdates"
              @click="updateConfigs"
            >
              Salvar Alterações
            </vs-button>
            <vs-button color="primary" type="border" @click="loadConfigs"
              >Atualizar</vs-button
            >
          </div>
        </div>
      </div>
      <vs-table
        :data="filteredConfigs"
        noDataText="Nenhuma configuração encontrada"
        class="tablePrincipal i4pro-table"
      >
        <template slot="thead">
          <vs-th>Config</vs-th>
          <vs-th>Field</vs-th>
          <vs-th>Valor</vs-th>
          <vs-th></vs-th>
        </template>
        <template slot-scope="{ data }">
          <tbody>
            <vs-tr v-for="item in data" :key="item.id" :data="item">
              <vs-td>
                <div class="i4pro-cell">
                  <p class="product-category">{{ item.config }}</p>
                </div>
              </vs-td>
              <vs-td>
                <div class="i4pro-cell">
                  <p class="product-category">{{ item.field }}</p>
                </div>
              </vs-td>
              <vs-td>
                <div class="i4pro-cell">
                  <div v-if="isBooleanDataType(item.dataType)">
                    <vs-switch
                      v-model="item.value"
                      color="success"
                      style="width: 80px"
                    >
                      <span slot="on">Sim</span>
                      <span slot="off">Não</span>
                    </vs-switch>
                  </div>
                  <div v-else-if="item.hasOptions">
                    <vs-select v-model="item.value" class="w-full">
                      <vs-select-item value="" text="Selecione" />
                      <vs-select-item
                        v-for="option in optionsForItem(item)"
                        :key="option.value"
                        :value="option.value"
                        :text="option.label"
                      />
                    </vs-select>
                  </div>
                  <div v-else>
                    <vs-input class="w-full" v-model="item.value" />
                  </div>
                </div>
              </vs-td>
              <vs-td></vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>
    </vx-card>
  </div>
</template>

<script>
import i4proApi from './../../../services/i4pro.api';
import notify from './../../../utils/notify';

export default {
  name: 'I4Pro',
  data() {
    return {
      configs: [],
      originalConfigValues: {},
      optionsByField: {},
      optionsLoading: {},
      dataTypeOptions: [
        { value: 'string', label: 'string' },
        { value: 'boolean', label: 'boolean' },
      ],
      defaultConfigs: ['URL_BASE', 'USER', 'PASS'],
    };
  },
  mounted() {
    this.loadConfigs();
  },
  computed: {
    filteredConfigs() {
      return this.configs.filter((item) => this.isConfigVisible(item));
    },
    updatedConfigs() {
      return this.configs.filter((item) => this.isConfigDirty(item));
    },
    hasPendingUpdates() {
      return this.updatedConfigs.length > 0;
    },
  },
  methods: {
    isBooleanDataType(dataType) {
      return String(dataType).toLowerCase() === 'boolean';
    },
    coerceBooleanValue(value) {
      if (typeof value === 'boolean') {
        return value;
      }
      if (typeof value === 'number') {
        return value === 1;
      }
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true' || value === '1';
      }
      return false;
    },
    normalizeConfig(item) {
      const normalized = { ...item };
      normalized.hasOptions = Boolean(normalized.hasOptions);
      if (this.isBooleanDataType(normalized.dataType)) {
        normalized.value = this.coerceBooleanValue(normalized.value);
      }
      if (
        !this.isBooleanDataType(normalized.dataType) &&
        normalized.value == null
      ) {
        normalized.value = '';
      }
      return normalized;
    },
    isConfigFilled(item) {
      if (!item) {
        return false;
      }
      if (typeof item.value === 'string') {
        return item.value.trim().length > 0;
      }
      if (typeof item.value === 'boolean') {
        return item.value === true;
      }
      return item.value !== null && item.value !== undefined;
    },
    cloneValue(value) {
      if (Array.isArray(value)) {
        return value.map((item) => this.cloneValue(item));
      }
      if (value && typeof value === 'object') {
        try {
          return JSON.parse(JSON.stringify(value));
        } catch (error) {
          return value;
        }
      }
      return value;
    },
    isSameValue(currentValue, originalValue) {
      if (currentValue === originalValue) {
        return true;
      }
      if (currentValue === null || currentValue === undefined) {
        return originalValue === null || originalValue === undefined;
      }
      if (originalValue === null || originalValue === undefined) {
        return false;
      }
      const currentType = typeof currentValue;
      const originalType = typeof originalValue;
      if (currentType === 'boolean' || originalType === 'boolean') {
        return String(currentValue) === String(originalValue);
      }
      if (currentType === 'number' || originalType === 'number') {
        return String(currentValue) === String(originalValue);
      }
      if (currentType === 'object' && originalType === 'object') {
        return JSON.stringify(currentValue) === JSON.stringify(originalValue);
      }
      return String(currentValue) === String(originalValue);
    },
    isConfigDirty(item) {
      if (!item || item.id === null || item.id === undefined) {
        return false;
      }
      return !this.isSameValue(item.value, this.originalConfigValues[item.id]);
    },
    setOriginalConfigValues(configs) {
      const valuesMap = {};
      configs.forEach((item) => {
        valuesMap[item.id] = this.cloneValue(item.value);
      });
      this.originalConfigValues = valuesMap;
    },
    isConfigFilledByName(configName) {
      const found = this.configs.find((item) => item.config === configName);
      return this.isConfigFilled(found);
    },
    findConfigByDependencyName(dependencyName) {
      return this.configs.find(
        (item) =>
          item.config === dependencyName || item.field === dependencyName,
      );
    },
    isDependencyFilled(dependencyName) {
      const found = this.findConfigByDependencyName(dependencyName);
      return this.isConfigFilled(found);
    },
    hasDefaultConfigsFilled() {
      return this.defaultConfigs.every((configName) =>
        this.isConfigFilledByName(configName),
      );
    },
    hasDependencyValue(value) {
      return value !== null && value !== undefined && value !== '';
    },
    getDependencyName(item) {
      if (item.hasDependency === null || item.hasDependency === undefined) {
        return null;
      }
      if (typeof item.hasDependency === 'string') {
        const trimmed = item.hasDependency.trim();
        return trimmed.length ? trimmed : null;
      }
      return item.hasDependency;
    },
    isConfigVisible(item) {
      if (this.defaultConfigs.includes(item.config)) {
        return true;
      }
      if (!this.hasDefaultConfigsFilled()) {
        return false;
      }
      const dependency = this.getDependencyName(item);
      if (!dependency) {
        return true;
      }
      if (Array.isArray(dependency)) {
        return dependency.every((configName) =>
          this.isDependencyFilled(configName),
        );
      }
      return this.isDependencyFilled(dependency);
    },
    getDependencyValue(item) {
      const dependency = this.getDependencyName(item);
      if (!dependency) {
        return null;
      }
      if (Array.isArray(dependency)) {
        const allFilled = dependency.every((configName) =>
          this.isDependencyFilled(configName),
        );
        if (!allFilled) {
          return null;
        }
        const firstName = dependency[0];
        const found = this.findConfigByDependencyName(firstName);
        return found ? found.value : null;
      }
      const found = this.findConfigByDependencyName(dependency);
      return this.isConfigFilled(found) ? found.value : null;
    },
    normalizeOptions(options) {
      if (!Array.isArray(options)) {
        return [];
      }
      const firstDefined = (values) => {
        for (let i = 0; i < values.length; i += 1) {
          if (values[i] !== null && values[i] !== undefined) {
            return values[i];
          }
        }
        return undefined;
      };
      return options.map((option) => {
        if (option && typeof option === 'object') {
          const value = firstDefined([
            option.value,
            option.id,
            option.code,
            option.codigo,
            option.key,
            option.label,
            option.name,
          ]);
          const label = firstDefined([
            option.label,
            option.name,
            option.description,
            option.descricao,
            value != null ? String(value) : '',
          ]);
          return { value, label };
        }
        return { value: option, label: option != null ? String(option) : '' };
      });
    },
    buildOptionsKey(item) {
      if (!item || !item.field) {
        return '';
      }
      const dependencyValue = this.getDependencyValue(item);
      if (this.hasDependencyValue(dependencyValue)) {
        return `${item.field}::${dependencyValue}`;
      }
      return item.field;
    },
    optionsForItem(item) {
      if (!item || !item.field) {
        return [];
      }
      const dependency = this.getDependencyName(item);
      if (dependency) {
        const dependencyValue = this.getDependencyValue(item);
        if (!this.hasDependencyValue(dependencyValue)) {
          return [];
        }
      }
      const key = this.buildOptionsKey(item);
      return this.optionsByField[key] || [];
    },
    async ensureOptions(item) {
      if (!item || !item.field) {
        return;
      }
      const dependency = this.getDependencyName(item);
      let dependencyValue;
      if (dependency) {
        dependencyValue = this.getDependencyValue(item);
        if (!this.hasDependencyValue(dependencyValue)) {
          return;
        }
      }
      const key = this.buildOptionsKey(item);
      if (!key || this.optionsByField[key] || this.optionsLoading[key]) {
        return;
      }
      this.$set(this.optionsLoading, key, true);
      try {
        const options = await i4proApi.getOptions(item.field, dependencyValue);
        this.$set(this.optionsByField, key, this.normalizeOptions(options));
      } catch (error) {
        notify.notifyFail(
          this.$vs,
          `Erro ao buscar opções do campo ${item.field}`,
        );
      } finally {
        this.$set(this.optionsLoading, key, false);
      }
    },
    async loadConfigs(silent = false) {
      if (!silent) {
        this.$vs.loading();
      }
      try {
        const data = await i4proApi.getConfigs();
        this.configs = (data || []).map((item) => this.normalizeConfig(item));
        this.setOriginalConfigValues(this.configs);
        const visibleConfigs = this.filteredConfigs;
        visibleConfigs
          .filter(
            (item) =>
              item.hasOptions &&
              !this.isBooleanDataType(item.dataType) &&
              item.field,
          )
          .forEach((item) => this.ensureOptions(item));
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao buscar configurações do I4Pro');
      } finally {
        if (!silent) {
          this.$vs.loading.close();
        }
      }
    },
    buildPayload(item) {
      return {
        config: item.config,
        value: this.isBooleanDataType(item.dataType)
          ? this.coerceBooleanValue(item.value)
          : item.value,
      };
    },
    async updateConfigs() {
      const items = this.updatedConfigs;
      if (!items.length) {
        return;
      }
      this.$vs.loading();
      try {
        for (const item of items) {
          const payload = this.buildPayload(item);
          await i4proApi.updateConfig(item.id, payload);
          this.$set(
            this.originalConfigValues,
            item.id,
            this.cloneValue(item.value),
          );
        }
        notify.notifySuccess(
          this.$vs,
          'Configurações atualizadas com sucesso.',
        );
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao atualizar configurações do I4Pro');
      } finally {
        this.$vs.loading.close();
      }
    },
    async deleteConfig(item) {
      if (!window.confirm('Deseja excluir esta configuração?')) {
        return;
      }
      this.$vs.loading();
      try {
        await i4proApi.deleteConfig(item.id);
        this.configs = this.configs.filter((config) => config.id !== item.id);
        this.$delete(this.originalConfigValues, item.id);
        notify.notifySuccess(this.$vs, 'Configuração removida com sucesso.');
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao remover configuração do I4Pro');
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>

<style scoped>
.i4pro-table /deep/ table {
  table-layout: fixed;
  width: 100%;
}

.i4pro-table /deep/ th,
.i4pro-table /deep/ td {
  width: 35%;
}

.i4pro-table /deep/ th:nth-child(2),
.i4pro-table /deep/ td:nth-child(2) {
  width: 15%;
}

.i4pro-table /deep/ th:nth-child(4),
.i4pro-table /deep/ td:nth-child(4) {
  width: 15%;
}

.i4pro-cell {
  max-height: 140px;
  overflow: auto;
}

.i4pro-actions {
  max-height: none;
  overflow: visible;
}
</style>
